import React, { useState, useEffect } from "react";
import { useGetDayWisePobDataMutation } from "state/api";
import { useTheme } from "@mui/material/styles";
import { RingLoader } from "react-spinners";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DownloadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import FlexBetween from "components/FlexBetween";
import * as XLSX from "xlsx";

const getMonthYearLabel = (month, year) => {
  const date = new Date(year, month - 1);
  const monthName = date.toLocaleString("default", { month: "long" });
  return `${monthName} ${year}`;
};

const getFinancialYearLabel = (month, year) => {
  const fyStartYear = month <= 3 ? year - 1 : year;
  const fyEndYear = fyStartYear + 1;
  return `${fyStartYear}-${fyEndYear.toString().slice(-2)}`;
};

const getMonthlyYearWiseData = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const current = {
    month: currentMonth,
    year: currentYear,
    offset: 0,
    label: `${getMonthYearLabel(currentMonth, currentYear)} – FY ${getFinancialYearLabel(currentMonth, currentYear)}`,
  };

  let lastMonth = currentMonth - 1;
  let lastMonthYear = currentYear;
  if (lastMonth === 0) {
    lastMonth = 12;
    lastMonthYear -= 1;
  }

  const last = {
    month: lastMonth,
    year: lastMonthYear,
    offset: -1,
    label: `${getMonthYearLabel(lastMonth, lastMonthYear)} – FY ${getFinancialYearLabel(lastMonth, lastMonthYear)}`,
  };

  return [current, last];
};

const DailyWisePob = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthYearLabel, setMonthYearLabel] = useState("");
  const [getDayWiseData] = useGetDayWisePobDataMutation();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [hierarchicalData, setHierarchicalData] = useState([]);
  const [openSections, setOpenSections] = useState({});

  // Updated filters without division
  const initialFilters = {
    rm: "",
    am: "",
    mr: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("hierarchy");

  const handleFetch = async (month, year, label) => {
    console.log("Fetching data for:", month, year);
    setLoading(true);

    try {
      const response = await getDayWiseData({ month, year });
      if (response?.data?.success) {
        setData(response.data.data);
        setMonthYearLabel(label);
        setError(null);
      } else {
        setFilteredData([]);
        setError("No data found for this month/year.");
      }
    } catch (error) {
      console.error("Error fetching day-wise data:", error);
      setError("An error occurred while fetching the data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const [current] = getMonthlyYearWiseData();
    handleFetch(current.month, current.year, current.label);
  }, []);

  const monthlyOptions = getMonthlyYearWiseData();

  useEffect(() => {
    if (data && data.length > 0) {
      const transformedData = (rawData) => {
        const hierarchy = {};
        const fields = Array.from({ length: 31 }, (_, i) => `Day_${i + 1}`);

        rawData.forEach((row) => {
          const parsedRow = {};
          fields.forEach((field) => {
            parsedRow[field] = parseInt(row[field]) || 0;
          });

          // Start hierarchy at RmMgName
          if (!hierarchy[row.RmMgName]) {
            hierarchy[row.RmMgName] = {
              RmMgName: row.RmMgName,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              children: {},
            };
          }

          const rm = hierarchy[row.RmMgName];
          fields.forEach((field) => (rm[field] += parsedRow[field]));

          if (!rm.children[row.AmMgName]) {
            rm.children[row.AmMgName] = {
              AmMgName: row.AmMgName,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              children: {},
            };
          }

          const am = rm.children[row.AmMgName];
          fields.forEach((field) => (am[field] += parsedRow[field]));

          if (!am.children[row.MrMgName]) {
            am.children[row.MrMgName] = {
              MrMgName: row.MrMgName,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              children: {},
            };
          }

          const mrMg = am.children[row.MrMgName];
          fields.forEach((field) => (mrMg[field] += parsedRow[field]));

          if (!mrMg.children[row.MrName]) {
            mrMg.children[row.MrName] = {
              MrName: row.MrName,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              children: {},
            };
          }

          const mr = mrMg.children[row.MrName];
          fields.forEach((field) => (mr[field] += parsedRow[field]));

          if (!mr.children[row.DoctName]) {
            mr.children[row.DoctName] = {
              DoctName: row.DoctName,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              children: {},
            };
          }

          const doct = mr.children[row.DoctName];
          fields.forEach((field) => (doct[field] += parsedRow[field]));

          if (!doct.children) doct.children = {};

          if (!doct.children[row.Chemist_name]) {
            doct.children[row.Chemist_name] = {
              Chemist_name: row.Chemist_name,
              ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
            };
          }

          const chemist = doct.children[row.Chemist_name];
          fields.forEach((field) => (chemist[field] += parsedRow[field]));
        });

        return Object.values(hierarchy).map((rm) => {
          rm.children = Object.values(rm.children).map((am) => {
            am.children = Object.values(am.children).map((mrMg) => {
              mrMg.children = Object.values(mrMg.children).map((mr) => {
                mr.children = Object.values(mr.children).map((doct) => {
                  doct.children = Object.values(doct.children).map(
                    (chemist) => ({
                      Chemist_name: chemist.Chemist_name,
                      ...fields.reduce(
                        (acc, field) => ({ ...acc, [field]: chemist[field] }),
                        {},
                      ),
                    }),
                  );

                  return {
                    DoctName: doct.DoctName,
                    ...fields.reduce(
                      (acc, field) => ({ ...acc, [field]: doct[field] }),
                      {},
                    ),
                    children: doct.children,
                  };
                });

                return {
                  MrName: mr.MrName,
                  ...fields.reduce(
                    (acc, field) => ({ ...acc, [field]: mr[field] }),
                    {},
                  ),
                  children: mr.children,
                };
              });

              return {
                MrMgName: mrMg.MrMgName,
                ...fields.reduce(
                  (acc, field) => ({ ...acc, [field]: mrMg[field] }),
                  {},
                ),
                children: mrMg.children,
              };
            });

            return {
              AmMgName: am.AmMgName,
              ...fields.reduce(
                (acc, field) => ({ ...acc, [field]: am[field] }),
                {},
              ),
              children: am.children,
            };
          });

          return {
            RmMgName: rm.RmMgName,
            ...fields.reduce(
              (acc, field) => ({ ...acc, [field]: rm[field] }),
              {},
            ),
            children: rm.children,
          };
        });
      };

      setHierarchicalData(transformedData(data));
    }
  }, [data]);

  useEffect(() => {
    if (hierarchicalData.length > 0) {
      const filtered = hierarchicalData
        .filter((rm) => !filters.rm || rm.RmMgName === filters.rm)
        .map((rm) => {
          const amChildren = rm.children
            .filter((am) => !filters.am || am.AmMgName === filters.am)
            .map((am) => {
              const mrMgChildren = am.children
                .filter((mrMg) => !filters.mr || mrMg.MrMgName === filters.mr)
                .map((mrMg) => {
                  const mrChildren = mrMg.children.map((mr) => {
                    const doctChildren = mr.children.map((doct) => {
                      const chemistChildren = doct.children.map((chemist) => ({
                        ...chemist,
                      }));
                      return { ...doct, children: chemistChildren };
                    });
                    return { ...mr, children: doctChildren };
                  });
                  return { ...mrMg, children: mrChildren };
                });
              return { ...am, children: mrMgChildren };
            });
          return { ...rm, children: amChildren };
        })
        .filter((rm) => rm.children.length > 0);

      setFilteredData(filtered);
    }
  }, [hierarchicalData, filters]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatValue = (value) => {
    return Number.isInteger(value) ? value : "-";
  };

  const renderRows = (data, level = 0) => {
    const colors = [
      theme.palette.secondary[100],
      theme.palette.secondary[300],
      theme.palette.secondary[500],
      theme.palette.secondary[700],
      theme.palette.grey[400],
      theme.palette.grey[300],
    ];

    return data.map((item, index) => {
      const id = `${level}-${index}`;
      const isOpen = openSections[id];
      const textColor = colors[level] || theme.palette.text.primary;

      // Mobile view
      if (isMobile) {
        return (
          <div
            key={id}
            style={{
              marginBottom: "10px",
              border: `1px solid ${theme.palette.secondary[300]}`,
              borderRadius: "8px",
              padding: "10px",
              backgroundColor: theme.palette.background.paper,
              cursor: "pointer",
            }}
          >
            <div
              onClick={() => toggleSection(id)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: "bold",
                color: textColor,
              }}
            >
              <span>
                {level === 0
                  ? item.RmMgName
                  : level === 1
                    ? item.AmMgName
                    : level === 2
                      ? item.MrMgName
                      : level === 3
                        ? item.MrName
                        : level === 4
                          ? item.DoctName
                          : item.Chemist_name}
              </span>
              {item.children && <span>{isOpen ? "▼" : "►"}</span>}
            </div>
            <div style={{ marginTop: "10px" }}>
              {Object.entries(item)
                .filter(([key]) => key !== "children" && key.startsWith("Day_"))
                .map(([key, value]) => (
                  <div key={key}>
                    <strong>{key}:</strong> {formatValue(value)}
                  </div>
                ))}
            </div>
            {isOpen && item.children && (
              <div style={{ marginLeft: "10px" }}>
                {renderRows(item.children, level + 1)}
              </div>
            )}
          </div>
        );
      }

      // Desktop view
      return (
        <React.Fragment key={id}>
          <TableRow
            onClick={() => toggleSection(id)}
            sx={{
              cursor: item.children ? "pointer" : "default",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            <TableCell
              sx={{
                paddingLeft: `${level * 20}px`,
                color: textColor,
                fontWeight: "bold",
              }}
            >
              {item.children && (
                <span style={{ marginRight: "5px" }}>{isOpen ? "▼" : "►"}</span>
              )}
              {level === 0
                ? item.RmMgName
                : level === 1
                  ? item.AmMgName
                  : level === 2
                    ? item.MrMgName
                    : level === 3
                      ? item.MrName
                      : level === 4
                        ? item.DoctName
                        : item.Chemist_name}
            </TableCell>
            {Array.from({ length: 31 }, (_, i) => (
              <TableCell key={i} align="right">
                {formatValue(item[`Day_${i + 1}`])}
              </TableCell>
            ))}
          </TableRow>
          {isOpen && item.children && renderRows(item.children, level + 1)}
        </React.Fragment>
      );
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.flatMap((rm) =>
        rm.children.flatMap((am) =>
          am.children.flatMap((mrmg) =>
            mrmg.children.flatMap((mr) =>
              mr.children.flatMap((doctor) =>
                doctor.children.map((chemist) => ({
                  RmMgName: rm.RmMgName,
                  AmMgName: am.AmMgName,
                  MrMgName: mrmg.MrMgName,
                  MrName: mr.MrName,
                  DoctName: doctor.DoctName,
                  Chemist_name: chemist.Chemist_name,
                  ...Object.fromEntries(
                    Array.from({ length: 31 }, (_, i) => [
                      `Day_${i + 1}`,
                      chemist[`Day_${i + 1}`],
                    ]),
                  ),
                })),
              ),
            ),
          ),
        ),
      ),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "POB Data");
    XLSX.writeFile(workbook, "pob_data.xlsx");
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <RingLoader color={theme.palette.primary.main} size={100} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p="1rem">
      <FlexBetween>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIosNew />}
          sx={{
            color: theme.palette.common.white,
            "&:hover": {
              background: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Back
        </Button>
        <Typography variant="h2" fontWeight="bold">
          Daily Wise POB
        </Typography>
        <FlexBetween gap="1rem">
          <Select
            value={monthYearLabel}
            onChange={(e) => {
              const selected = monthlyOptions.find(
                (opt) => opt.label === e.target.value,
              );
              if (selected)
                handleFetch(selected.month, selected.year, selected.label);
            }}
            sx={{ minWidth: 250 }}
          >
            {monthlyOptions.map((option) => (
              <MenuItem key={option.label} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Button variant="contained" onClick={() => setFilterDialogOpen(true)}>
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlined />}
            onClick={downloadExcel}
          >
            Export
          </Button>
        </FlexBetween>
      </FlexBetween>

      <Box mt={2}>
        {filteredData.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "80vh", overflow: "auto" }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                    }}
                  >
                    Hierarchy
                  </TableCell>
                  {Array.from({ length: 31 }, (_, i) => (
                    <TableCell
                      key={i}
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                      }}
                    >
                      {i + 1}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderRows(filteredData)}</TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h6" textAlign="center" p={3}>
            No data available
          </Typography>
        )}
      </Box>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
      >
        <DialogTitle>Apply Filters</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            minWidth={300}
            mt={1}
          >
            <FormControl fullWidth>
              <InputLabel>RM</InputLabel>
              <Select
                name="rm"
                value={filters.rm}
                onChange={handleFilterChange}
                label="RM"
              >
                <MenuItem value="">All RM</MenuItem>
                {Array.from(
                  new Set(hierarchicalData.map((item) => item.RmMgName)),
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>AM</InputLabel>
              <Select
                name="am"
                value={filters.am}
                onChange={handleFilterChange}
                label="AM"
              >
                <MenuItem value="">All AM</MenuItem>
                {Array.from(
                  new Set(
                    hierarchicalData.flatMap((rm) =>
                      rm.children.map((am) => am.AmMgName),
                    ),
                  ),
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>MR</InputLabel>
              <Select
                name="mr"
                value={filters.mr}
                onChange={handleFilterChange}
                label="MR"
              >
                <MenuItem value="">All MR</MenuItem>
                {Array.from(
                  new Set(
                    hierarchicalData.flatMap((rm) =>
                      rm.children.flatMap((am) =>
                        am.children.map((mrmg) => mrmg.MrMgName),
                      ),
                    ),
                  ),
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyWisePob;
